import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, LayoutDashboard } from 'lucide-react';
import { portalMe, portalRates, portalOrders } from '../../api/resources';
import { inr, fmtDate } from '../../utils/format';
import PageHeader from '../../components/ui/PageHeader';
import Spinner from '../../components/ui/Spinner';
import { WORK_LABEL } from './shared';

function Stat({ label, value, sub, tone = 'default' }) {
  const color = {
    default: 'text-brand-900',
    green: 'text-success-600',
    amber: 'text-warning-700',
    muted: 'text-content-muted',
  }[tone];
  return (
    <div className="card p-4">
      <p className="text-2xs font-semibold uppercase tracking-wider text-content-subtle">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${color}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-content-subtle">{sub}</p>}
    </div>
  );
}

// A job count that opens the list behind it. Red when something is late —
// that is the one a tailor must not miss.
function WorkTile({ to, label, value, sub, tone }) {
  const tones = {
    brand: 'border-brand-500 text-brand-700',
    amber: 'border-warning-500 text-warning-700',
    red: 'border-danger-500 text-danger-600',
    green: 'border-success-500 text-success-700',
  };
  const hot = tone === 'red' && value > 0;
  return (
    <Link
      to={to}
      className={`card border-l-4 p-4 transition-shadow hover:shadow-pop ${tones[tone]} ${hot ? 'bg-danger-50' : ''}`}
    >
      <p className="text-2xs font-semibold uppercase tracking-wider text-content-subtle">{label}</p>
      <p className="mt-1 text-3xl font-bold tabular-nums">{value}</p>
      <p className="mt-0.5 text-xs text-content-subtle">{sub}</p>
    </Link>
  );
}

export default function PortalHome() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['portal-me'], queryFn: portalMe });
  const { data: rateData, isLoading: ratesLoading } = useQuery({ queryKey: ['portal-rates'], queryFn: portalRates });
  // Counts only — the orders themselves live on the My Orders page.
  const { data: workData } = useQuery({ queryKey: ['portal-orders', 'pending', ''], queryFn: () => portalOrders({ view: 'pending' }) });

  if (isLoading) return <Spinner label="Loading your dashboard…" />;
  if (isError || !data) {
    return <p className="card p-8 text-center text-sm text-danger-600">Could not load your dashboard. Please sign in again.</p>;
  }

  const e = data.earnings;
  const rates = rateData?.rates || [];
  // The same four buckets as My Orders, so the numbers agree everywhere.
  const work = workData?.counts || {};

  return (
    <div className="space-y-5">
      <PageHeader
        icon={LayoutDashboard}
        eyebrow="My Work"
        title={`Hello, ${data.worker.name}`}
        subtitle="Your earnings, payouts and rate card"
        actions={(
          <Link to="/portal/orders" className="btn-primary">
            <ClipboardList size={15} /> My Orders
          </Link>
        )}
      />

      {/* My work — the four questions, each opening the matching list. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <WorkTile to="/portal/orders?view=pending" label="Pending" value={work.pending ?? 0} sub="waiting on you" tone="brand" />
        <WorkTile to="/portal/orders?view=today" label="Due today" value={work.today ?? 0} sub="deliver today" tone="amber" />
        <WorkTile to="/portal/orders?view=overdue" label="Overdue" value={work.overdue ?? 0} sub="past the delivery date" tone="red" />
        <WorkTile to="/portal/orders?view=completed" label="Completed" value={work.completed ?? 0} sub="finished by you" tone="green" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total Earnings" value={inr(e.earned.amount)} sub={`${e.earned.count} completed job(s)`} />
        <Stat label="Settled" value={inr(e.settled.amount)} sub={`${e.settled.count} job(s) paid out`} tone="green" />
        <Stat label="Pending" value={inr(e.pending.amount)} sub={`${e.pending.count} job(s) awaiting payout`} tone="amber" />
        <Stat label="In Progress" value={inr(e.inProgress.amount)} sub="Earned once completed" tone="muted" />
      </div>

      {data.lastSettlement && (
        <p className="text-sm text-content-muted">
          Last payout <b className="text-content">{data.lastSettlement.settlementNo}</b> of{' '}
          <b className="text-content">{inr(data.lastSettlement.amount)}</b> on {fmtDate(data.lastSettlement.createdAt)}.{' '}
          <Link to="/portal/payouts" className="font-semibold text-brand-700 hover:underline">View payouts</Link>
        </p>
      )}

      <section className="card overflow-hidden">
        <div className="border-b border-line px-4 py-3">
          <h3 className="font-display text-lg font-semibold text-brand-900">My Rate Card</h3>
          <p className="text-xs text-content-subtle">
            What you earn per piece. Set by your administrator — contact them if something looks wrong.
          </p>
        </div>
        {ratesLoading && <Spinner />}
        {!ratesLoading && rates.length === 0 && (
          <p className="py-8 text-center text-sm text-content-subtle">No rates are set up yet.</p>
        )}
        {!ratesLoading && rates.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th">Item</th>
                  <th className="th">Work</th>
                  <th className="th text-right">Rate / piece</th>
                  <th className="th">Basis</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={`${r.tailoringItemId}-${r.workType}`} className="row">
                    <td className="td font-medium">{r.itemName}</td>
                    <td className="td text-content-muted">{WORK_LABEL[r.workType]}</td>
                    <td className="td text-right font-semibold tabular-nums">{inr(r.rate)}</td>
                    <td className="td text-xs text-content-subtle">
                      {r.source === 'PERSONAL' ? (r.basis || 'Your personal rate') : 'Standard shop rate'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
